import { Component, ErrorInfo, ReactNode } from "react"
import { Button, Result } from "antd"

interface Props {
  children: ReactNode
  // When this value changes, a boundary that is currently showing the fallback
  // resets itself — lets the user navigate away from a crashed screen without a
  // full page reload (the nav lives outside the boundary and keeps working).
  resetKey?: unknown
}

interface State {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("Unhandled render error:", error, info.componentStack)
  }

  public componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: undefined })
    }
  }

  public render() {
    if (this.state.hasError) {
      return (
        <Result
          status="error"
          title="Something went wrong"
          subTitle={this.state.error?.message || "This screen failed to render. Try again or pick another page."}
          extra={(
            <Button type="primary" onClick={() => this.setState({ hasError: false, error: undefined })}>
              Try again
            </Button>
          )}
        />
      )
    }

    return this.props.children
  }
}
